import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import {
  Loader2,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  Zap,
  BookOpen,
  Flag,
  LifeBuoy,
  AlertTriangle,
} from "lucide-react";

import {
  GET_AI_LOGS,
  GET_AVATAR_LIBRARY,
  GET_FLAG_COUNTS,
} from "@/graphql/queries/admin";
import { GET_SUPPORT_REQUESTS } from "@/graphql/queries/support";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth/AuthContext";

export default function AdminDashboardPage() {
  const { devTools } = useAuth();
  const { data, loading } = useQuery(GET_AI_LOGS, {
    variables: { limit: 1, offset: 0 },
  });
  const { data: flagData } = useQuery(GET_FLAG_COUNTS);
  const flagCounts = (flagData as any)?.flagCounts || {};
  const flagTotal = (flagCounts.exercises || 0) + (flagCounts.theory || 0);

  // Derived from the open rows, so answering the queue clears the badge. A count
  // somebody has to remember to reset is one that stops being read.
  const { data: supportData } = useQuery(GET_SUPPORT_REQUESTS, {
    variables: { status: "open" },
  });
  const openSupport = (supportData as any)?.openSupportRequests || 0;

  // Avatars a child was drawn that the repository does not have. Derived from the
  // rows, so it clears itself the moment `rake avatars:fetch` is committed — see
  // AvatarLibrary.
  const { data: avatarData } = useQuery(GET_AVATAR_LIBRARY);
  const avatarLibrary = (avatarData as any)?.avatarLibrary;
  const avatarsPending: number = avatarLibrary?.pending ?? 0;
  const pendingStyles: string[] = Object.entries(
    (avatarLibrary?.pendingByStyle ?? {}) as Record<string, number>,
  ).map(([style, count]) => `${style} (${count})`);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const stats = data?.aiLogs?.stats ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Administravimas</h1>
        <p className="text-muted-foreground">
          Sistemos administravimo ir stebėjimo įrankiai
        </p>
      </div>

      {/* Only when there is something to do about it: a card that is always on
          screen is one nobody reads. */}
      {avatarsPending > 0 && (
        <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Nauji avatarai dar ne repozitorijoje
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="text-2xl font-bold text-orange-600">
              {avatarsPending}
            </div>
            <p className="text-sm text-muted-foreground">
              Tiek paveikslėlių buvo sugeneruota AI, nes jų nebuvo bibliotekoje
              {pendingStyles.length > 0 && <>: {pendingStyles.join(", ")}</>}.
              Paleisk <code className="font-mono">rake avatars:fetch</code> ir
              užkomituok <code className="font-mono">public/images/avatars</code>
              {" "}— tada šis įrašas išnyks pats.
            </p>
            <p className="text-xs text-muted-foreground">
              Bibliotekoje šiuo metu: {avatarLibrary?.files ?? 0} paveikslėliai.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI užklausos</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalCalls ?? 0}</div>
            <p className="text-xs text-muted-foreground">Visos AI užklausos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sėkmingos</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats?.successfulCalls ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Sėkmingos užklausos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Klaidos</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats?.failedCalls ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Nepavykusios užklausos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vid. trukmė</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.avgDurationMs
                ? `${(stats.avgDurationMs / 1000).toFixed(1)}s`
                : "0s"}
            </div>
            <p className="text-xs text-muted-foreground">
              Vidutinė užklausos trukmė
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Token Usage */}
      {stats && stats.totalTokensUsed > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Tokenu naudojimas
            </CardTitle>
            <Zap className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalTokensUsed.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Viso sunaudota tokenu
            </p>
          </CardContent>
        </Card>
      )}

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle>Administravimo įrankiai</CardTitle>
          <CardDescription>
            Greiti nuorodas i administravimo funkcijas
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Development only, like the page itself — see `DevTools`. */}
          {devTools && (
            <Link to="/admin/exercises">
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-4"
              >
                <BookOpen className="mr-3 h-5 w-5" />
                <div className="text-left">
                  <div className="font-medium">Užduotys</div>
                  <div className="text-sm text-muted-foreground">
                    Peržiūrėkite visas statines užduotis
                  </div>
                </div>
              </Button>
            </Link>
          )}
          <Link to="/admin/flagged">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
            >
              <Flag className="mr-3 h-5 w-5 text-orange-500" />
              <div className="text-left">
                <div className="font-medium">
                  Turinys peržiūrai
                  {flagTotal > 0 && (
                    <span className="ml-2 rounded-full bg-orange-500 px-1.5 py-0.5 text-xs text-white">
                      {flagTotal}
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  Mokinių pranešimai, netikslios užduotys ir per menka teorija
                </div>
              </div>
            </Button>
          </Link>
          <Link to="/admin/support">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
            >
              <LifeBuoy className="mr-3 h-5 w-5 text-primary" />
              <div className="text-left">
                <div className="font-medium">
                  Pagalbos užklausos
                  {openSupport > 0 && (
                    <span className="ml-2 rounded-full bg-orange-500 px-1.5 py-0.5 text-xs text-white">
                      {openSupport}
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  Ką žmonės parašė per „Reikia pagalbos?“
                </div>
              </div>
            </Button>
          </Link>
          <Link to="/admin/logs">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
            >
              <Activity className="mr-3 h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">AI žurnalai</div>
                <div className="text-sm text-muted-foreground">
                  Peržiūrėkite visas AI užklausas ir atsakymus
                </div>
              </div>
            </Button>
          </Link>
          <Link to="/settings">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4"
            >
              <Zap className="mr-3 h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">Nustatymai</div>
                <div className="text-sm text-muted-foreground">
                  API raktai ir kiti nustatymai
                </div>
              </div>
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
